import Vue from "vue";
import XwOrderRefundDialog from "@/components/XwOrderRefundDialog.vue";

const DEFAULT_FREIGHT_KEYS = [
  "sourceOrderFreightAmount",
  "fatherFreightAmount",
  "freightAmount",
  "pickupFreightAmount",
  "sourceOrderPickupFreightAmount",
  "fatherPickupFreightAmount",
];

function toNum(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickFirstNum(source, keys) {
  if (!source || !keys || !keys.length) return null;
  for (let i = 0; i < keys.length; i += 1) {
    const n = toNum(source[keys[i]]);
    if (n != null) return n;
  }
  return null;
}

function roundMoney(n) {
  return Number(Number(n).toFixed(2));
}

/**
 * 解析鉴别/寄售「子单」退款展示金额。
 * 口径始终是当前子单，不用父单合单实付/已退（多瓶子单会把兄弟单金额算进来）。
 * 实付 = 本子单鉴别/服务费 + 可归属到本子单的寄件运费。
 */
export function resolveXwOrderAmounts(row, opts = {}) {
  if (!row) {
    return {
      servicePrice: 0,
      freightAmount: 0,
      paidAmount: 0,
      refundedAmount: 0,
      remainingAmount: 0,
    };
  }

  const priceKey = opts.priceKey || "price";
  const paidAmountKey = opts.paidAmountKey || "paidAmount";
  const refundedAmountKey = opts.refundedAmountKey || "refundedAmount";
  const freightKeys = opts.freightKeys || DEFAULT_FREIGHT_KEYS;

  let servicePrice = pickFirstNum(row, [
    opts.servicePriceKey,
    "servicePrice",
    priceKey,
    "sourceOrderPrice",
  ].filter(Boolean)) || 0;
  const extraJb = pickFirstNum(row, ["extraJbAmount", "sourceOrderExtraJbAmount"]) || 0;
  if (extraJb > 0) {
    servicePrice = roundMoney(servicePrice + extraJb);
  }

  // 寄件运费只信后端明确字段，禁止用「父单实付-子单服务费」反推。
  let freightAmount = pickFirstNum(row, ["freightAmount"].concat(freightKeys)) || 0;
  if (freightAmount <= 0) {
    freightAmount = pickFirstNum(row, ["fatherFreightAmount"]) || 0;
  }
  if (freightAmount <= 0) {
    const fatherOrder = row.fatherOrder || row.fatherorder;
    if (fatherOrder) {
      freightAmount = pickFirstNum(fatherOrder, freightKeys.concat(["fatherFreightAmount"])) || 0;
    }
  }

  const fatherCombinedPaid = pickFirstNum(row, [
    "sourceOrderFatherPaidAmount",
    "fatherOrderPrice",
  ]);
  // 运费挂在父单：仅当父单实付≈本子单服务费+运费（单瓶）时并入本子单；多瓶合单不把整笔运费算进当前子单。
  if (freightAmount > 0 && fatherCombinedPaid != null) {
    const singleChildImplied = roundMoney(servicePrice + freightAmount);
    if (Math.abs(fatherCombinedPaid - singleChildImplied) > 0.021) {
      freightAmount = 0;
    }
  }

  // 子单实付：优先子单 paidAmount；否则 服务费+可归属运费。绝不用父单合单实付。
  let paidAmount = pickFirstNum(row, [
    paidAmountKey,
    "sourceOrderPaidAmount",
    "paidAmount",
  ]);
  if (paidAmount == null || paidAmount <= 0) {
    paidAmount = roundMoney(servicePrice + freightAmount);
  } else {
    paidAmount = roundMoney(paidAmount);
    if (freightAmount > 0 && paidAmount <= servicePrice + 0.001) {
      paidAmount = roundMoney(servicePrice + freightAmount);
    } else if (extraJb > 0 && paidAmount <= servicePrice - extraJb + 0.001) {
      // paid_amount 快照可能未含附收鉴别费
      paidAmount = roundMoney(paidAmount + extraJb);
    }
  }

  // 已退也只看本子单，不用父单 refunded_amount
  const refundedAmount = pickFirstNum(row, [
    refundedAmountKey,
    "sourceOrderRefundedAmount",
    "refundedAmount",
  ]) || 0;

  const remainingAmount = Math.max(0, roundMoney(paidAmount - refundedAmount));

  return {
    servicePrice: roundMoney(servicePrice),
    freightAmount: roundMoney(freightAmount),
    paidAmount,
    refundedAmount: roundMoney(refundedAmount),
    remainingAmount,
  };
}

/**
 * 打开鉴别/寄售子单退款弹窗
 * @param {Object} options
 * @param {string} options.orderNo 展示用订单号
 * @param {string} [options.orderId] 提交用订单号，默认同 orderNo
 * @param {number|string} options.refundAmount 默认退款金额 / 实付兜底
 * @param {number|string} [options.paidAmount] 实付金额
 * @param {number|string} [options.servicePrice] 鉴别/服务费
 * @param {number|string} [options.freightAmount] 寄件运费
 * @param {number|string} [options.refundedAmount] 已退金额
 * @param {string} [options.orderTypeLabel] 订单类型文案
 * @param {string} [options.consignMode] REUSE_IDENTIFY 等
 * @returns {Promise<{orderId:string, refundAmount:number, closeOrder:boolean, refundReason?:string}>}
 */
export function openXwOrderRefundDialog(options = {}) {
  return new Promise((resolve, reject) => {
    const orderNo = options.orderNo || options.orderId || "";
    const orderTypeLabel =
      options.orderTypeLabel || resolveOrderTypeLabel(options);
    const amounts = resolveXwOrderAmounts(options);

    const DialogCtor = Vue.extend(XwOrderRefundDialog);
    const instance = new DialogCtor({
      propsData: {
        visible: true,
        orderNo,
        orderTypeLabel,
        refundAmount: options.refundAmount != null ? options.refundAmount : amounts.remainingAmount,
        servicePrice: amounts.servicePrice,
        freightAmount: amounts.freightAmount,
        paidAmount: amounts.paidAmount,
        refundedAmount: amounts.refundedAmount,
      },
    });

    const mountEl = document.createElement("div");
    document.body.appendChild(mountEl);
    instance.$mount(mountEl);

    let settled = false;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      instance.$destroy();
      if (instance.$el && instance.$el.parentNode) {
        instance.$el.parentNode.removeChild(instance.$el);
      }
      fn();
    };

    instance.$on("confirm", (form) => {
      finish(() =>
        resolve({
          orderId: options.orderId || orderNo,
          ...form,
        })
      );
    });
    instance.$on("cancel", () => {
      finish(() => reject("cancel"));
    });
  });
}

/** @deprecated 请使用 openXwOrderRefundDialog */
export function promptXwOrderRefund(orderLabel) {
  return openXwOrderRefundDialog({ orderNo: orderLabel, refundAmount: 0 }).then(
    (form) => form.closeOrder
  );
}

export function requestXwOrderRefund(https, apiUrl, params) {
  const payload =
    typeof params === "object" && params != null
      ? params
      : {
          orderId: arguments[2],
          refundAmount: arguments[3],
          closeOrder: arguments[4],
        };
  return https(
    apiUrl,
    {
      orderId: payload.orderId,
      refundAmount: payload.refundAmount,
      closeOrder: !!payload.closeOrder,
      refundReason: payload.refundReason,
    },
    "POST"
  );
}

/**
 * 列表页一键退款：打开弹窗并提交
 */
export function runXwOrderRefund(vm, apiUrl, row, mapRow) {
  const meta = mapRow ? mapRow(row) : buildDefaultRowMeta(row);
  if (meta.refundAmount == null && meta.paidAmount == null) {
    vm.$message.warning("缺少订单金额，无法退款");
    return Promise.resolve();
  }
  return openXwOrderRefundDialog(meta)
    .then((form) =>
      requestXwOrderRefund(vm.$https.bind(vm), apiUrl, form)
    )
    .then((res) => {
      if (res.data && res.data.code === 200) {
        vm.$message.success((res.data && res.data.msg) || "退款成功");
        return res;
      }
      vm.$message.error((res.data && res.data.msg) || "退款失败");
      return Promise.reject(res);
    })
    .catch((err) => {
      if (err !== "cancel") {
        return Promise.reject(err);
      }
    });
}

function buildDefaultRowMeta(row) {
  const amounts = resolveXwOrderAmounts(row);
  return {
    orderId: row.orderId,
    orderNo: row.orderId,
    refundAmount: amounts.remainingAmount > 0 ? amounts.remainingAmount : amounts.servicePrice,
    ...amounts,
    consignMode: row.consignMode,
    sfjs: row.sfjs,
  };
}

function resolveOrderTypeLabel(options) {
  if (options.orderTypeLabel) return options.orderTypeLabel;
  if (String(options.sfjs) === "1") {
    return options.consignMode === "REUSE_IDENTIFY" ? "寄售·防伪码" : "寄售";
  }
  if (options.sfjs != null) return "鉴别";
  return "";
}

/** 鉴别/寄售子单：已取消 status */
export const XW_ORDER_STATUS_CANCELLED = 10;

/** 未支付 */
export const XW_ORDER_STATUS_UNPAID = -1;

/** 已退款 */
export const XW_ORDER_STATUS_REFUNDED = 5;

/**
 * 列表是否展示「退款」：已支付且未关单/未全额退完，不限于「已寄出」。
 * status=5 不直接拦截：不符退回(ptjb=1) 仅改状态为 5，未必已微信退款，以 is_closed 与可退余额为准。
 */
export function canRefundXwOrder(row, opts = {}) {
  if (!row) return false;
  const statusKey = opts.statusKey || "status";
  const closedKey = opts.closedKey || "isClosed";
  const orderIdKey = opts.orderIdKey || "orderId";
  const orderId = row[orderIdKey] || row.orderNo;
  if (!orderId) return false;

  const status = Number(row[statusKey]);
  if (
    status === XW_ORDER_STATUS_UNPAID
    || status === XW_ORDER_STATUS_CANCELLED
  ) {
    return false;
  }
  if (Number(row[closedKey]) === 1) return false;

  const amounts = resolveXwOrderAmounts(row, opts);
  if (amounts.paidAmount > 0 && amounts.remainingAmount <= 0) {
    return false;
  }

  if (opts.predicate && !opts.predicate(row)) return false;
  return true;
}

/** 是否可关单（不退款） */
export function canCloseXwOrder(row, opts = {}) {
  if (!row) return false;
  const statusKey = opts.statusKey || "status";
  const closedKey = opts.closedKey || "isClosed";
  const orderId = row[opts.orderIdKey || "orderId"] || row.orderNo;
  if (!orderId) return false;
  const status = Number(row[statusKey]);
  if (status === 5 || status === XW_ORDER_STATUS_CANCELLED) return false;
  if (Number(row[closedKey]) === 1) return false;
  if (opts.predicate && !opts.predicate(row)) return false;
  return true;
}

/**
 * 列表页关单：不自动退款
 */
export function runXwOrderClose(vm, apiUrl, row, mapRow, opts) {
  const meta = mapRow ? mapRow(row) : { orderId: row.orderId, orderNo: row.orderId };
  if (!meta.orderId) {
    vm.$message.warning("缺少订单号");
    return Promise.resolve();
  }
  const label = meta.orderNo || meta.orderId;
  return vm
    .$confirm(
      `确认取消订单 ${label}？\n取消后不自动退款，订单 status 将变为「已取消」(10)，并标记为已关单。`,
      "取消订单",
      {
        confirmButtonText: "下一步",
        cancelButtonText: "取消",
        type: "warning",
      }
    )
    .then(() =>
      vm.$prompt("取消原因（选填）", `取消订单 ${label}`, {
        confirmButtonText: "确认取消",
        cancelButtonText: "取消",
        inputPlaceholder: "如：用户放弃、异常处理",
      })
    )
    .then(({ value }) =>
      vm.$https(
        apiUrl,
        {
          orderId: meta.orderId,
          closeReason: value || undefined,
        },
        "POST"
      )
    )
    .then((res) => {
      if (res.data && res.data.code === 200) {
        vm.$message.success((res.data && res.data.msg) || "取消成功");
        return res;
      }
      vm.$message.error((res.data && res.data.msg) || "取消失败");
      return Promise.reject(res);
    })
    .catch((err) => {
      if (err !== "cancel" && err !== "close") {
        return Promise.reject(err);
      }
    });
}



// WEBPACK FOOTER //
// ./src/util/xwOrderRefund.js